<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAIPlan } from './composables/useAIPlan'
import type { ScoreResult } from '@/features/scoring/lib/types'
import type { PlanData } from './lib/types'

const props = withDefaults(defineProps<{
  scoreOverride?: ScoreResult | null
  contextLabel?: string
  persistActivePlan?: boolean
  showLoadAction?: boolean
  showImportShortcut?: boolean
  showExportActions?: boolean
}>(), {
  scoreOverride: null,
  contextLabel: '',
  persistActivePlan: true,
  showLoadAction: true,
  showImportShortcut: true,
  showExportActions: true,
})

const emit = defineEmits<{
  planGenerated: [plan: PlanData]
}>()

const importFileEl = ref<HTMLInputElement | null>(null)
const showRaw      = ref(false)
const prefsOpen    = ref(true)
const scoreOverrideRef = computed(() => props.scoreOverride)

const {
  testDate, focus, level, daysPerWeek, injuries, equipment,
  isPregnancyMode, pregnancyPhase,
  hasScore, status, isLoading, rawText, planSummary, parseStatus,
  errorMsg, currentModel,
  generate,
} = useAIPlan({
  scoreOverride: scoreOverrideRef,
  persistActivePlan: props.persistActivePlan,
  syncActivePlan: props.persistActivePlan,
  saveToSelectedUfpmMember: props.persistActivePlan,
  onPlanGenerated: plan => emit('planGenerated', plan),
})

const daysUntilTest = computed<number | null>(() => {
  if (!testDate.value) return null
  return Math.max(0, Math.round((new Date(testDate.value).getTime() - Date.now()) / 86400000))
})

const btnLabel = computed(() =>
  isLoading.value
    ? `⚡ Generating${currentModel.value ? ' (' + currentModel.value + ')' : ''}…`
    : '⚡ Generate My Workout Plan'
)

const canRetry = computed(() => {
  const msg = errorMsg.value.toLowerCase()
  return msg.includes('rate limit')
    || msg.includes('rate limited')
    || msg.includes('all models rate limited')
    || errorMsg.value.includes('429')
    || msg.includes('generated plan schedule did not match')
})

function loadPlan() {
  window.activatePlan?.()
}

function handleImportFile(event: Event) {
  window.importPlanJson?.(event)
  // Reset so the same file can be re-imported without another file-picker interaction
  setTimeout(() => {
    const el = event.target as HTMLInputElement
    if (el) el.value = ''
  }, 200)
}

function exportJson() { window.exportPlanJson?.() }
function exportXlsx() { window.exportPlanXlsx?.() }

function downloadRaw() {
  if (!rawText.value) return
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([rawText.value], { type: 'text/plain' }))
  a.download = 'usaf_pt_workout_plan.txt'
  a.click()
}
</script>

<template>
  <!-- No score warning -->
  <div
    v-if="!hasScore && !isPregnancyMode"
    class="alert alert-warn"
    style="margin-bottom:12px;"
  >
    &#9888;
    <template v-if="contextLabel">
      No score saved for this member yet &mdash; administer a UFPM test before generating a plan.
    </template>
    <template v-else>
      No score calculated yet &mdash; go to the Dashboard tab, enter your results, and press Calculate Score.
    </template>
  </div>

  <div v-if="contextLabel" class="alert alert-info" style="margin-bottom:12px;">
    Generating for {{ contextLabel }}. This plan will stay in UFPM until exported or imported by the member.
  </div>

  <!-- Plan Preferences accordion -->
  <div class="acc-card" style="margin-bottom:10px;">
    <div class="acc-header" @click="prefsOpen = !prefsOpen">
      <div class="acc-header-left">
        <span class="acc-title">Plan Preferences</span>
        <span class="acc-pts">optional</span>
      </div>
      <span class="acc-tap-hint">{{ prefsOpen ? 'tap to collapse' : 'tap to expand' }}</span>
      <span class="acc-chevron" :class="{ open: prefsOpen }" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>
    </div>
    <div v-show="prefsOpen" class="acc-body">
      <!-- Pregnancy / Postpartum mode toggle -->
      <div class="field" style="margin-bottom:10px;">
        <label style="display:flex;align-items:flex-start;flex-wrap:wrap;gap:8px;cursor:pointer;font-weight:600;">
          <input
            type="checkbox"
            v-model="isPregnancyMode"
            style="width:16px;height:16px;cursor:pointer;"
          />
          Generate Pregnancy or Postpartum Plan (uses official USAF perinatal guidance)
        </label>
      </div>

      <!-- Phase selector (shown only in pregnancy mode) -->
      <div v-if="isPregnancyMode" class="field" style="margin-bottom:14px;">
        <label for="pregnancy-phase">Phase</label>
        <select id="pregnancy-phase" v-model="pregnancyPhase">
          <option value="">Select pregnancy or postpartum phase…</option>
          <option value="t1">1st Trimester</option>
          <option value="t2">2nd Trimester</option>
          <option value="t3">3rd Trimester</option>
          <option value="postpartum-standard">Postpartum &ndash; Standard Delivery</option>
          <option value="postpartum-csection">Postpartum &ndash; C-Section</option>
        </select>
      </div>

      <div class="row">
        <div class="field">
          <label>Official Test Date</label>
          <input type="date" v-model="testDate" />
          <div style="font-size:11px;color:var(--muted);margin-top:5px;">
            <template v-if="daysUntilTest !== null">
              {{ daysUntilTest }} day{{ daysUntilTest !== 1 ? 's' : '' }} until test
            </template>
          </div>
        </div>
        <div v-if="!isPregnancyMode" class="field">
          <label>Focus Area</label>
          <select v-model="focus">
            <option value="balanced">Balanced (all components)</option>
            <option value="cardio">Cardio heavy</option>
            <option value="strength">Strength heavy</option>
            <option value="body">Body composition</option>
          </select>
        </div>
        <div v-if="!isPregnancyMode" class="field">
          <label>Fitness Level</label>
          <select v-model="level">
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div v-if="!isPregnancyMode" class="field">
          <label>Days per week</label>
          <select v-model="daysPerWeek">
            <option :value="3">3 days/week</option>
            <option :value="4">4 days/week</option>
            <option :value="5">5 days/week</option>
            <option :value="6">6 days/week</option>
          </select>
        </div>
      </div>
      <div class="field">
        <label>Injuries or limitations?</label>
        <input
          type="text"
          v-model="injuries"
          placeholder="e.g. knee pain, lower back (leave blank if none)"
        />
      </div>
      <div v-if="!isPregnancyMode" class="field">
        <label>Equipment available</label>
        <div class="toggle-group">
          <button
            class="toggle-btn"
            :class="{ active: equipment === 'bodyweight' }"
            type="button"
            @click="equipment = 'bodyweight'"
          >Bodyweight Only</button>
          <button
            class="toggle-btn"
            :class="{ active: equipment === 'gym' }"
            type="button"
            @click="equipment = 'gym'"
          >Gym + Bodyweight</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Generate button -->
  <button
    class="btn btn-primary"
    type="button"
    :disabled="isLoading"
    @click="generate"
    style="width:100%;font-size:15px;padding:14px 28px;margin-bottom:12px;"
  >
    {{ btnLabel }}
  </button>

  <!-- Loading state -->
  <div
    v-if="isLoading"
    class="ai-loading-card"
    role="status"
    aria-live="polite"
  >
    <div class="ai-loading-header">
      <div class="ai-loading-equalizer" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div>
        <div class="ai-loading-title">Building your training plan</div>
        <div class="ai-loading-subtitle">
          {{ currentModel ? 'Using ' + currentModel : 'Preparing request' }}
        </div>
      </div>
    </div>
    <div class="ai-loading-track" aria-hidden="true">
      <span></span>
    </div>
    <div class="ai-loading-steps" aria-hidden="true">
      <span>Reviewing score</span>
      <span>Balancing workouts</span>
      <span>Formatting plan</span>
    </div>
  </div>

  <!-- Import Plan JSON shortcut -->
  <div v-if="showImportShortcut" style="display:flex;justify-content:center;margin-bottom:12px;">
    <button
      class="btn btn-secondary"
      type="button"
      @click="importFileEl?.click()"
      style="font-size:11px;"
    >Import Plan JSON</button>
  </div>
  <div v-if="showImportShortcut" style="text-align:center;font-size:11px;color:var(--muted);margin:-6px 0 12px;">
    JSON is the app-loadable plan file. Importing it makes that plan active in Training after confirmation.
  </div>

  <!-- Source guide note -->
  <div style="text-align:center;margin-bottom:16px;">
    <template v-if="!isPregnancyMode">
      <span style="font-size:11px;color:var(--muted);">Plans follow the </span>
      <a
        href="https://www.afpc.af.mil/Portals/70/documents/FITNESS/Tab%204.%20The%20Warfighter%27s%20Fitness%20Playbook%20%202.0%20Feb%202026.pdf"
        target="_blank"
        rel="noopener"
        style="font-size:11px;color:var(--accent);font-weight:600;text-decoration:none;"
      >Warfighter's Fitness Playbook 2.0</a>
      <span style="font-size:11px;color:var(--muted);"> - PFRA 1 Mar 2026 - v1.2</span>
    </template>
    <template v-else>
      <span style="font-size:11px;color:var(--muted);">Plans follow official </span>
      <a
        href="https://www.afpc.af.mil/Portals/70/documents/FITNESS/Tab%205.%20Pregnancy%20&%20Postpardum%20Performance%20Training%20Guide.pdf"
        target="_blank"
        rel="noopener"
        style="font-size:11px;color:var(--accent);font-weight:600;text-decoration:none;"
      >USAF Pregnancy &amp; Postpartum Fitness Guidance</a>
    </template>
  </div>

  <!-- Error banner -->
  <div
    v-if="status === 'error'"
    class="alert alert-error"
    style="margin-bottom:16px;"
  >
    &#10007; {{ errorMsg }}
    <button
      v-if="canRetry"
      type="button"
      @click="generate"
      style="margin-left:10px;background:var(--surface2);color:var(--accent);border:1px solid var(--accent);padding:4px 12px;border-radius:3px;cursor:pointer;font-size:11px;"
    >&#8635; Retry</button>
  </div>

  <!-- Result card (visible after successful generation or parse failure) -->
  <div v-if="status === 'success' || status === 'parse-error'">
    <div class="card">
      <div class="card-title" style="justify-content:space-between;flex-wrap:wrap;gap:8px;">
        <span>{{ status === 'success' ? 'Plan Ready!' : 'Parse Failed' }}</span>
        <span
          v-if="parseStatus"
          style="font-size:11px;color:var(--muted);"
        >{{ parseStatus }}</span>
      </div>

      <!-- Plan Ready actions -->
      <template v-if="status === 'success'">
        <div style="font-size:13px;color:var(--green);margin-bottom:12px;">
          &#10003; Plan parsed successfully &mdash; {{ planSummary }}
        </div>
        <div style="display:flex;gap:10px;flex-wrap:wrap;">
          <button
            v-if="showLoadAction"
            class="btn btn-primary"
            type="button"
            @click="loadPlan"
            style="font-size:14px;padding:11px 22px;"
          >&#9654; Load Plan into Workout Log</button>
          <button
            v-if="showExportActions"
            class="btn btn-secondary"
            type="button"
            @click="exportJson"
            style="font-size:11px;"
          >Export JSON</button>
          <button
            v-if="showExportActions"
            class="btn btn-secondary"
            type="button"
            @click="exportXlsx"
            style="font-size:11px;"
          >Export Excel</button>
          <button
            v-if="showImportShortcut"
            class="btn btn-secondary"
            type="button"
            @click="importFileEl?.click()"
            style="font-size:11px;"
          >Import JSON</button>
          <button
            class="btn btn-secondary"
            type="button"
            @click="showRaw = !showRaw"
            style="font-size:11px;"
          >&#128065; Raw Output</button>
        </div>
      </template>

      <!-- Parse Failed actions -->
      <template v-if="status === 'parse-error'">
        <div style="font-size:13px;color:var(--orange);margin-bottom:10px;">
          Could not parse plan &mdash; tap Retry to regenerate.
        </div>
        <div style="display:flex;gap:10px;flex-wrap:wrap;">
          <button
            class="btn btn-secondary"
            type="button"
            @click="showRaw = !showRaw"
            style="font-size:11px;"
          >&#128065; View Raw Output</button>
          <button
            class="btn btn-secondary"
            type="button"
            @click="downloadRaw"
            style="font-size:11px;"
          >&#8595; Download .txt</button>
        </div>
      </template>

      <!-- Raw output (shared between both result states) -->
      <div
        v-show="showRaw"
        id="ai-output"
        style="font-size:12px;line-height:1.8;white-space:pre-wrap;color:var(--muted);max-height:200px;overflow-y:auto;border:1px solid var(--border);border-radius:3px;padding:10px;background:var(--bg);margin-top:12px;"
      >{{ rawText }}</div>
    </div>
  </div>

  <!--
    Hidden file input — ALWAYS mounted (no v-if).
    The Log tab's vanilla "Import Plan JSON" button uses
    getElementById('plan-import-file').click() and must find this element at any time.
  -->
  <input
    ref="importFileEl"
    id="plan-import-file"
    type="file"
    accept=".json,application/json"
    style="display:none;"
    @change="handleImportFile"
  />

  <!-- Short disclaimer (fixed at bottom of panel) -->
  <div style="position:sticky;bottom:0;background:var(--bg);padding-top:8px;border-top:1px solid #2a3540;margin-top:12px;">
    <p style="font-size:11px;color:#6f8192;text-align:center;">
      Unofficial tool • Not affiliated with the U.S. Air Force
    </p>
  </div>
</template>

<style scoped>
.ai-loading-card {
  margin: 0 0 14px;
  padding: 14px;
  border: 1px solid rgba(160, 120, 64, 0.42);
  border-radius: 6px;
  background:
    linear-gradient(135deg, rgba(160, 120, 64, 0.10), transparent 58%),
    var(--surface);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
}

.ai-loading-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ai-loading-title {
  color: var(--fg);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
  text-transform: uppercase;
}

.ai-loading-subtitle {
  margin-top: 3px;
  color: var(--muted);
  font-size: 12px;
}

.ai-loading-equalizer {
  display: flex;
  align-items: end;
  justify-content: center;
  gap: 3px;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(160, 120, 64, 0.45);
  border-radius: 6px;
  background: rgba(160, 120, 64, 0.08);
  flex: 0 0 34px;
}

.ai-loading-equalizer span {
  width: 4px;
  height: 10px;
  border-radius: 2px;
  background: var(--accent);
  animation: ai-loading-pulse 0.9s ease-in-out infinite;
}

.ai-loading-equalizer span:nth-child(2) { animation-delay: 0.12s; }
.ai-loading-equalizer span:nth-child(3) { animation-delay: 0.24s; }
.ai-loading-equalizer span:nth-child(4) { animation-delay: 0.36s; }

.ai-loading-track {
  position: relative;
  overflow: hidden;
  height: 5px;
  margin-top: 13px;
  border-radius: 999px;
  background: rgba(160, 120, 64, 0.18);
}

.ai-loading-track span {
  position: absolute;
  inset: 0 auto 0 0;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  animation: ai-loading-scan 1.35s ease-in-out infinite;
}

.ai-loading-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
  color: var(--muted);
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.ai-loading-steps span {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes ai-loading-pulse {
  0%, 100% { height: 8px; opacity: 0.48; }
  50% { height: 24px; opacity: 1; }
}

@keyframes ai-loading-scan {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(250%); }
}

@media (max-width: 420px) {
  .ai-loading-steps {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-loading-equalizer span,
  .ai-loading-track span {
    animation: none;
  }

  .ai-loading-equalizer span {
    height: 16px;
    opacity: 0.85;
  }
}
</style>
